// Coordinates this provider instance. Cross-tab/backend concurrency is a separate gate.
const validUser = value => !!value && typeof value === 'object' && !Array.isArray(value)
  && typeof value.id === 'string' && value.id.length > 0 && value.id.length <= 128
  && typeof value.role === 'string' && value.role.length > 0
  && !['deleted','inactive','blocked','suspended'].includes(value.status);
const validLogin = data => validUser(data?.user) && typeof data.access_token === 'string'
  && data.access_token.length <= 16384 && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(data.access_token)
  && (data.must_change_password === undefined || typeof data.must_change_password === 'boolean');
const cancelled = () => new Error('La sesión cambió. Inténtalo nuevamente.');

export function createSessionCoordinator({ api, storage, stopTracking, stopPush, stopGoogle = () => Promise.resolve(), stopApple = () => Promise.resolve(), changed = () => {}, publish }) {
  let alive = true, epoch = 0, tail = Promise.resolve(), loginPending = false, logoutPending = null, quarantine = false, issuedToken = null, pendingLoginCancellation = null;
  const emit = value => { if (alive) publish(value); };
  const current = ticket => alive && epoch === ticket;
  async function clearLocal() {
    let cleared = true;
    emit({ user: false, loading: false });
    try { await storage.removeItem('ca_token'); changed(); } catch { cleared = false; }
    emit({ user: false, loading: false });
    return cleared;
  }
  function queue(work) {
    const task = tail.then(work, work);
    tail = task.catch(() => {});
    return task;
  }
  async function refresh() {
    if (!alive || loginPending || logoutPending || quarantine) return false;
    const ticket = ++epoch;
    let prepared = false;
    try {
      if (storage.ready) await storage.ready();
      prepared = true;
      if (!current(ticket)) return false;
      const { data } = await api.get('/auth/me');
      if (!current(ticket)) return false;
      if (!validUser(data)) throw new Error('Invalid user');
      emit({ user: data, loading: false }); return data;
    } catch (error) {
      if (current(ticket) && !prepared) {
        quarantine = true;
        await Promise.allSettled([Promise.resolve().then(stopTracking), Promise.resolve().then(() => stopPush({ logout: true }))]);
        emit({ user: false, loading: false, warning: 'No pudimos abrir el acceso seguro. Reintenta el cierre de sesión antes de entrar.' });
        return false;
      }
      if (current(ticket) && error?.response?.status === 401) { await logout(); return false; }
      if (current(ticket)) emit({ user: false, loading: false });
      return false;
    }
  }
  async function login(endpoint, payload, { signal, cancelAttempt, expectedUserId, requireOk = false, requireMfaVerified = false, replaceNativeSession = false } = {}) {
    if (!alive || loginPending || logoutPending || quarantine || signal?.aborted || (requireOk && !expectedUserId)) throw cancelled();
    loginPending = true;
    const ticket = ++epoch;
    let aborted = false, attemptToken = null;
    if (cancelAttempt) pendingLoginCancellation = cancelAttempt;
    const onAbort = () => {
      aborted = true;
      if (current(ticket)) ++epoch;
      quarantine = true;
      emit({ user: false, loading: false });
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    // Do not abort the exchange HTTP request: its response may contain the
    // just-issued bearer that must be revoked after navigation/cancellation.
    const attempt = queue(async () => {
      try {
        if (storage.ready) await storage.ready();
        if (!current(ticket)) throw cancelled();
        if (replaceNativeSession) {
          // Stop the previous device streams while their own token is still in
          // storage. Do not publish a temporary logout that unmounts this login.
          const previous = storage.getItem?.('ca_token');
          if (previous) {
            const stopped = await Promise.allSettled([
              Promise.resolve().then(stopTracking),
              Promise.resolve().then(() => stopPush({ logout: true })),
            ]);
            if (stopped.some(result => result.status !== 'fulfilled')) throw new Error('Completa el cierre de la cuenta anterior antes de entrar.');
            const revoked = await api.post('/auth/logout', {}, { timeout: 10000, headers: { Authorization: `Bearer ${previous}` } });
            if (revoked?.data?.ok !== true) throw new Error('No pudimos confirmar el cierre de la cuenta anterior.');
            await storage.removeItem('ca_token');
            changed();
            issuedToken = null;
          }
          if (!current(ticket)) throw cancelled();
        }
        const { data } = await api.post(endpoint, payload);
        if (typeof data?.access_token === 'string' && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(data.access_token) && data.access_token.length <= 16384) issuedToken = attemptToken = data.access_token;
        if (!current(ticket)) throw cancelled();
        if (!validLogin(data) || (requireOk && data?.ok !== true) || (requireMfaVerified && data?.user?.mfa_required !== false)
            || (expectedUserId && data.user.id !== expectedUserId)) {
          // A cookie may have been issued even though the body cannot be trusted.
          quarantine = true;
          await clearLocal();
          emit({ warning: 'No pudimos verificar la sesión. Completa el cierre antes de volver a entrar.' });
          throw new Error('Respuesta de acceso inválida.');
        }
        try { await storage.setItem('ca_token', data.access_token); changed(); }
        catch {
          quarantine = true; await clearLocal();
          emit({ warning: 'No pudimos guardar o limpiar el acceso local. Completa el cierre de sesión.' });
          throw new Error('Acceso local no disponible.');
        }
        if (!current(ticket)) { await clearLocal(); throw cancelled(); }
        const user = { ...data.user, must_change_password: data.must_change_password ?? !!data.user.must_change_password };
        if (pendingLoginCancellation === cancelAttempt) pendingLoginCancellation = null;
        emit({ user, loading: false, warning: null }); return user;
      } finally {
        signal?.removeEventListener('abort', onAbort);
        loginPending = false;
      }
    });
    return attempt.catch(async reason => {
      // Run outside the serialized attempt to avoid waiting on our own queue.
      // Keep quarantine if remote revocation or local/native cleanup fails.
      if (aborted || cancelAttempt || replaceNativeSession) {
        quarantine = true;
        await logout({ token: attemptToken });
      }
      throw reason;
    });
  }
  function logout({ token: attemptToken } = {}) {
    if (logoutPending) return logoutPending;
    ++epoch; quarantine = true;
    if (attemptToken) issuedToken = attemptToken;
    else { try { issuedToken = storage.getItem?.('ca_token') || issuedToken; } catch {} }
    const earlyCleanup = clearLocal();
    logoutPending = queue(async () => {
      await earlyCleanup;
      // Each cleanup is attempted even if another rejects, including synchronous throws.
      const results = await Promise.allSettled([
        Promise.resolve().then(stopTracking),
        Promise.resolve().then(() => stopPush({ logout: true })),
        Promise.resolve().then(() => api.post('/auth/logout', {}, { timeout: 10000, headers: issuedToken ? { Authorization: `Bearer ${issuedToken}` } : {} })),
        Promise.resolve().then(stopGoogle),
        Promise.resolve().then(stopApple),
        Promise.resolve().then(async () => {
          const cancel = pendingLoginCancellation;
          if (!cancel) return true;
          const confirmed = await cancel();
          if (confirmed && pendingLoginCancellation === cancel) pendingLoginCancellation = null;
          return confirmed === true;
        }),
      ]);
      const localCleared = await clearLocal();
      const remoteConfirmed = results[2].status === 'fulfilled' && results[2].value?.data?.ok === true;
      let pushStopped = results[1].status === 'fulfilled';
      const revokedSession = remoteConfirmed && results[2].value?.data?.native_push_revoked_session;
      if (revokedSession && typeof revokedSession.jti === 'string' && typeof revokedSession.user_id === 'string') {
        // Native bindings are session-fenced server-side. Reconcile uncertain
        // registry cleanup only after the server confirms revocation, then retry
        // local provider cleanup. A network error is never revocation proof.
        try { await stopPush({ logout: true, revokedSession }); pushStopped = true; }
        catch { pushStopped = false; }
      }
      const nativeStopped = results[0].status === 'fulfilled' && pushStopped && results[3].status === 'fulfilled' && results[4].status === 'fulfilled';
      if (remoteConfirmed && localCleared) issuedToken = null;
      const attemptCancelled = results[5].status === 'fulfilled' && results[5].value === true;
      quarantine = !(localCleared && nativeStopped && remoteConfirmed && attemptCancelled);
      emit({ warning: quarantine ? 'El cierre no pudo confirmarse por completo. Vuelve a intentarlo antes de entrar.' : null });
      return { localCleared, nativeStopped, remoteConfirmed, complete: !quarantine };
    }).finally(() => { logoutPending = null; });
    return logoutPending;
  }
  return { refresh, login, logout,
    dispose() { alive = false; ++epoch; },
    resume() { alive = true; },
  };
}

